import { useCallback } from "react";
import { makeVar, ReactiveVar, useReactiveVar } from "@apollo/client";
import { Products, Properties } from "graphql/generated/graphql";

export interface CartItemType {
  product: Products;
  property: Properties;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface CartStatusType {
  items: CartItemType[];
  totalItems: number;
  totalAmount: number;
}

const cartInitialState: CartStatusType = {
  items: [],
  totalItems: 0,
  totalAmount: 0,
};

export const cartVar: ReactiveVar<CartStatusType> =
  makeVar<CartStatusType>(cartInitialState);

export const clearCartStatus = () => {
  cartVar(cartInitialState);
};

export const useCartStatus = () => {
  const cartValue = useReactiveVar(cartVar);

  const calculateTotals = (items: CartItemType[]) => {
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = items.reduce((sum, item) => sum + item.totalPrice, 0);
    return { totalItems, totalAmount };
  };

  const addToCart = useCallback(
    (
      product: Products,
      property: Properties,
      quantity: number,
      unitPrice: number
    ) => {
      const currentItems = [...cartValue.items];

      // Search if the item already exists (same combination of product and property)
      const existingItemIndex = currentItems.findIndex(
        (item) =>
          item.product.id === product.id && item.property.id === property.id
      );

      if (existingItemIndex !== -1) {
        // Update quantity if item already exists
        currentItems[existingItemIndex].quantity += quantity;
        currentItems[existingItemIndex].totalPrice =
          currentItems[existingItemIndex].quantity *
          currentItems[existingItemIndex].unitPrice;
      } else {
        //  Add new item
        currentItems.push({
          product,
          property,
          quantity,
          unitPrice,
          totalPrice: quantity * unitPrice,
        });
      }

      const { totalItems, totalAmount } = calculateTotals(currentItems);

      cartVar({
        items: currentItems,
        totalItems,
        totalAmount,
      });
    },
    [cartValue]
  );

  const updateQuantity = useCallback(
    (productId: string, propertyId: string, quantity: number) => {
      const currentItems = [...cartValue.items];

      const itemIndex = currentItems.findIndex(
        (item) =>
          item.product.id === productId && item.property.id === propertyId
      );

      if (itemIndex !== -1) {
        currentItems[itemIndex].quantity = quantity;
        currentItems[itemIndex].totalPrice =
          quantity * currentItems[itemIndex].unitPrice;

        const { totalItems, totalAmount } = calculateTotals(currentItems);

        cartVar({
          items: currentItems,
          totalItems,
          totalAmount,
        });
      }
    },
    [cartValue]
  );

  const removeFromCart = useCallback(
    (productId: string, propertyId: string) => {
      const currentItems = cartValue.items.filter(
        (item) =>
          !(item.product.id === productId && item.property.id === propertyId)
      );

      const { totalItems, totalAmount } = calculateTotals(currentItems);

      cartVar({
        items: currentItems,
        totalItems,
        totalAmount,
      });
    },
    [cartValue]
  );

  return {
    cartStatusValue: cartValue,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCartStatus,
  };
};
